import { create } from "zustand";
import {
  trainingPlansApi,
  coachApi,
  type ChatMessage,
} from "../lib/api";
import { streamChat } from "../lib/sse";
import { useAuthStore } from "./authStore";
import type {
  TrainingPlan,
  PlannedSession,
  PlanSuggestion,
  MetricSuggestion,
  GoalMetric,
  SessionChange,
} from "@runlens/shared";

/** Strip complete and partial ```json blocks from streaming coach text */
function stripJsonBlocks(text: string): string {
  // Remove complete ```json ... ``` blocks
  let result = text.replace(/```json\s*[\s\S]*?\s*```/g, "");
  // Remove partial/in-progress ```json block (opened but not closed yet)
  result = result.replace(/```json[\s\S]*$/g, "");
  return result.trim();
}

/** Get today's date key in YYYY-MM-DD */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

interface PlanState {
  // Current plan data
  activePlan: TrainingPlan | null;
  sessions: PlannedSession[];
  metrics: GoalMetric[];
  isLoading: boolean;
  error: string | null;

  // Plan list
  plans: TrainingPlan[];
  isLoadingPlans: boolean;

  // Coach chat state
  coachMessages: ChatMessage[];
  coachSessions: string[];
  coachActiveDate: string;
  isCoachReadOnly: boolean;
  isCoachLoading: boolean;
  coachError: string | null;
  pendingSuggestion: PlanSuggestion | null;
  readyForSuggestion: boolean;
  pendingMetricSuggestion: MetricSuggestion | null;
  readyForMetricSuggestion: boolean;

  // Actions
  fetchPlans: () => Promise<void>;
  fetchPlan: (planId: string) => Promise<void>;
  createPlan: (name: string) => Promise<TrainingPlan>;
  updatePlan: (planId: string, data: Partial<TrainingPlan>) => Promise<void>;
  archivePlan: (planId: string) => Promise<void>;
  updateSessions: (changes: SessionChange[]) => Promise<void>;
  triggerMatch: () => Promise<void>;

  // Coach actions
  loadCoachSessions: (planId: string) => Promise<void>;
  selectCoachDate: (date: string) => Promise<void>;
  loadCoachHistory: (planId: string) => Promise<void>;
  sendCoachMessage: (message: string) => Promise<void>;
  startCoachConversation: () => Promise<void>;
  acceptSuggestion: () => Promise<void>;
  declineSuggestion: () => Promise<void>;
  acceptMetricSuggestion: () => Promise<void>;
  declineMetricSuggestion: () => Promise<void>;
  clearCoachHistory: () => Promise<void>;

  // Reset
  resetPlan: () => Promise<void>;
  reset: () => void;
}

export const usePlanStore = create<PlanState>((set, get) => ({
  // Initial state
  activePlan: null,
  sessions: [],
  metrics: [],
  isLoading: false,
  error: null,
  plans: [],
  isLoadingPlans: false,
  coachMessages: [],
  coachSessions: [],
  coachActiveDate: todayKey(),
  isCoachReadOnly: false,
  isCoachLoading: false,
  coachError: null,
  pendingSuggestion: null,
  readyForSuggestion: false,
  pendingMetricSuggestion: null,
  readyForMetricSuggestion: false,

  fetchPlans: async () => {
    set({ isLoadingPlans: true });
    try {
      const { plans } = await trainingPlansApi.list();
      set({ plans, isLoadingPlans: false });
    } catch (error) {
      set({ isLoadingPlans: false });
      throw error;
    }
  },

  fetchPlan: async (planId: string) => {
    set({ isLoading: true, error: null });
    try {
      const plan = await trainingPlansApi.get(planId);
      set({
        activePlan: plan,
        sessions: plan.sessions,
        metrics: plan.metrics || [],
        isLoading: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load plan";
      set({ isLoading: false, error: message });
    }
  },

  createPlan: async (name: string) => {
    const plan = await trainingPlansApi.create({ name });
    set((state) => ({
      plans: [plan, ...state.plans],
      activePlan: plan,
      sessions: [],
    }));
    return plan;
  },

  updatePlan: async (planId: string, data: Partial<TrainingPlan>) => {
    const updated = await trainingPlansApi.update(planId, data);
    set((state) => ({
      activePlan: state.activePlan?.id === planId ? updated : state.activePlan,
      plans: state.plans.map((p) => (p.id === planId ? updated : p)),
    }));
  },

  archivePlan: async (planId: string) => {
    await trainingPlansApi.delete(planId);
    set((state) => ({
      activePlan: state.activePlan?.id === planId ? null : state.activePlan,
      sessions: state.activePlan?.id === planId ? [] : state.sessions,
      plans: state.plans.filter((p) => p.id !== planId),
    }));
  },

  updateSessions: async (changes: SessionChange[]) => {
    const { activePlan } = get();
    if (!activePlan) return;

    await trainingPlansApi.updateSessions(activePlan.id, changes);

    // Refresh plan to get updated sessions
    const plan = await trainingPlansApi.get(activePlan.id);
    set({
      activePlan: plan,
      sessions: plan.sessions,
    });
  },

  triggerMatch: async () => {
    const { activePlan } = get();
    if (!activePlan) return;

    await trainingPlansApi.match(activePlan.id);

    // Refresh plan to get matched sessions
    const plan = await trainingPlansApi.get(activePlan.id);
    set({
      activePlan: plan,
      sessions: plan.sessions,
    });
  },

  loadCoachSessions: async (planId: string) => {
    try {
      const { sessions } = await coachApi.getSessions(planId);
      set({ coachSessions: sessions });
    } catch {
      // Non-critical
    }
  },

  selectCoachDate: async (date: string) => {
    const { activePlan } = get();
    if (!activePlan) return;

    const today = todayKey();
    set({
      coachActiveDate: date,
      isCoachReadOnly: date !== today,
      coachMessages: [],
      coachError: null,
      pendingSuggestion: null,
      readyForSuggestion: false,
      pendingMetricSuggestion: null,
      readyForMetricSuggestion: false,
    });

    set({ isCoachLoading: true });
    try {
      const { messages, pendingSuggestion, readyForSuggestion, pendingMetricSuggestion, readyForMetricSuggestion } = await coachApi.getHistory(activePlan.id, date);
      set({
        coachMessages: messages,
        isCoachLoading: false,
        // Only show suggestions for today's session
        pendingSuggestion: date === today ? (pendingSuggestion || null) : null,
        readyForSuggestion: date === today ? (readyForSuggestion ?? false) : false,
        pendingMetricSuggestion: date === today ? (pendingMetricSuggestion || null) : null,
        readyForMetricSuggestion: date === today ? (readyForMetricSuggestion ?? false) : false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load chat";
      set({ isCoachLoading: false, coachError: message });
    }
  },

  loadCoachHistory: async (planId: string) => {
    set({ isCoachLoading: true, coachError: null });
    try {
      const activeDate = get().coachActiveDate;
      const { messages, pendingSuggestion, readyForSuggestion, pendingMetricSuggestion, readyForMetricSuggestion } = await coachApi.getHistory(planId, activeDate);
      set({
        coachMessages: messages,
        isCoachLoading: false,
        pendingSuggestion: pendingSuggestion || null,
        readyForSuggestion: readyForSuggestion ?? false,
        pendingMetricSuggestion: pendingMetricSuggestion || null,
        readyForMetricSuggestion: readyForMetricSuggestion ?? false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load chat";
      set({ isCoachLoading: false, coachError: message });
    }
  },

  sendCoachMessage: async (message: string) => {
    const { activePlan, coachMessages, isCoachLoading, isCoachReadOnly } = get();
    if (!activePlan || isCoachLoading || isCoachReadOnly) return;

    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;

    // User message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
    };

    // Streaming assistant placeholder
    const streamingId = `streaming-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    set({
      coachMessages: [...coachMessages, userMessage, assistantPlaceholder],
      isCoachLoading: true,
      coachError: null,
    });

    let rawAccumulated = "";

    try {
      await streamChat(
        `/coach/${activePlan.id}`,
        { message },
        token,
        {
          onChunk: (text) => {
            rawAccumulated += text;
            const display = stripJsonBlocks(rawAccumulated);
            set((state) => ({
              coachMessages: state.coachMessages.map((m) =>
                m.id === streamingId
                  ? { ...m, content: display }
                  : m
              ),
            }));
          },
          onDone: (metadata) => {
            const suggestion = (metadata?.suggestion as PlanSuggestion) || null;
            const metricSuggestion = (metadata?.metricSuggestion as MetricSuggestion) || null;

            set((state) => {
              // If message is empty (AI returned only JSON), use explanation as text
              const fallbackExplanation = suggestion?.explanation || metricSuggestion?.explanation;
              const needsFallback = fallbackExplanation &&
                state.coachMessages.some((m) => m.id === streamingId && !m.content.trim());

              return {
                isCoachLoading: false,
                pendingSuggestion: suggestion,
                readyForSuggestion: (metadata?.readyForSuggestion as boolean) ?? false,
                pendingMetricSuggestion: metricSuggestion,
                readyForMetricSuggestion: (metadata?.readyForMetricSuggestion as boolean) ?? false,
                ...(needsFallback ? {
                  coachMessages: state.coachMessages.map((m) =>
                    m.id === streamingId
                      ? { ...m, content: fallbackExplanation! }
                      : m
                  ),
                } : {}),
              };
            });

            // Refresh sessions list
            get().loadCoachSessions(activePlan.id);
          },
          onError: (error) => {
            set((state) => ({
              coachMessages: state.coachMessages.filter(
                (m) => m.id !== streamingId && m.id !== userMessage.id
              ),
              isCoachLoading: false,
              coachError: error.message,
            }));
          },
        },
      );
    } catch (error) {
      const errMessage =
        error instanceof Error ? error.message : "Failed to send message";
      set((state) => ({
        coachMessages: state.coachMessages.filter(
          (m) => m.id !== streamingId && m.id !== userMessage.id
        ),
        isCoachLoading: false,
        coachError: errMessage,
      }));
    }
  },

  startCoachConversation: async () => {
    const { activePlan, isCoachLoading, isCoachReadOnly } = get();
    if (!activePlan || isCoachLoading || isCoachReadOnly) return;

    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;

    // Streaming assistant placeholder
    const streamingId = `streaming-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    set({
      coachMessages: [assistantPlaceholder],
      isCoachLoading: true,
      coachError: null,
    });

    try {
      await streamChat(
        `/coach/${activePlan.id}/start`,
        {},
        token,
        {
          onChunk: (text) => {
            set((state) => ({
              coachMessages: state.coachMessages.map((m) =>
                m.id === streamingId
                  ? { ...m, content: m.content + text }
                  : m
              ),
            }));
          },
          onDone: () => {
            set({ isCoachLoading: false });

            // Refresh sessions list
            get().loadCoachSessions(activePlan.id);
          },
          onError: (error) => {
            set({
              coachMessages: [],
              isCoachLoading: false,
              coachError: error.message,
            });
          },
        },
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to start conversation";
      set({ coachMessages: [], isCoachLoading: false, coachError: message });
    }
  },

  acceptSuggestion: async () => {
    const { activePlan, pendingSuggestion } = get();
    if (!activePlan || !pendingSuggestion) return;

    await get().updateSessions(pendingSuggestion.changes);

    const statusMsg: ChatMessage = {
      id: `system-${Date.now()}`,
      role: "system",
      content: "Plan updated with suggested changes",
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      pendingSuggestion: null,
      readyForSuggestion: false,
      coachMessages: [...state.coachMessages, statusMsg],
    }));

    // Persist: strip JSON + save system message to Redis
    coachApi.processSuggestion(activePlan.id, "accept").catch(() => {});
  },

  declineSuggestion: async () => {
    const { activePlan } = get();
    if (!activePlan) return;

    const statusMsg: ChatMessage = {
      id: `system-${Date.now()}`,
      role: "system",
      content: "Suggestion dismissed",
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      pendingSuggestion: null,
      readyForSuggestion: false,
      coachMessages: [...state.coachMessages, statusMsg],
    }));

    // Persist: strip JSON + save system message to Redis
    coachApi.processSuggestion(activePlan.id, "decline").catch(() => {});
  },

  acceptMetricSuggestion: async () => {
    const { activePlan, pendingMetricSuggestion } = get();
    if (!activePlan || !pendingMetricSuggestion) return;

    try {
      await coachApi.processMetricSuggestion(activePlan.id, "accept", pendingMetricSuggestion.metrics);

      // Refresh plan to get new metrics
      const plan = await trainingPlansApi.get(activePlan.id);

      const statusMsg: ChatMessage = {
        id: `system-${Date.now()}`,
        role: "system",
        content: "Metrics added to plan",
        timestamp: new Date().toISOString(),
      };

      set((state) => ({
        metrics: plan.metrics || [],
        pendingMetricSuggestion: null,
        readyForMetricSuggestion: false,
        coachMessages: [...state.coachMessages, statusMsg],
      }));
    } catch (err) {
      console.error("Failed to accept metric suggestion:", err);
      set({ coachError: "Failed to save metrics. Please try again." });
    }
  },

  declineMetricSuggestion: async () => {
    const { activePlan } = get();
    if (!activePlan) return;

    const statusMsg: ChatMessage = {
      id: `system-${Date.now()}`,
      role: "system",
      content: "Metric suggestion dismissed",
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      pendingMetricSuggestion: null,
      readyForMetricSuggestion: false,
      coachMessages: [...state.coachMessages, statusMsg],
    }));

    coachApi.processMetricSuggestion(activePlan.id, "decline").catch(() => {});
  },

  clearCoachHistory: async () => {
    const { activePlan, coachActiveDate } = get();
    if (!activePlan) return;

    await coachApi.clearHistory(activePlan.id, coachActiveDate);
    set({ coachMessages: [], pendingSuggestion: null, readyForSuggestion: false, pendingMetricSuggestion: null, readyForMetricSuggestion: false });

    // Refresh sessions list
    get().loadCoachSessions(activePlan.id);
  },

  resetPlan: async () => {
    const { activePlan } = get();
    if (!activePlan) return;

    set({ isLoading: true });
    try {
      await trainingPlansApi.reset(activePlan.id);
      set({
        sessions: [],
        metrics: [],
        coachMessages: [],
        coachSessions: [],
        coachActiveDate: todayKey(),
        isCoachReadOnly: false,
        pendingSuggestion: null,
        readyForSuggestion: false,
        pendingMetricSuggestion: null,
        readyForMetricSuggestion: false,
        isLoading: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to reset plan";
      set({ isLoading: false, error: message });
    }
  },

  reset: () => {
    set({
      activePlan: null,
      sessions: [],
      metrics: [],
      isLoading: false,
      error: null,
      plans: [],
      isLoadingPlans: false,
      coachMessages: [],
      coachSessions: [],
      coachActiveDate: todayKey(),
      isCoachReadOnly: false,
      isCoachLoading: false,
      coachError: null,
      pendingSuggestion: null,
      readyForSuggestion: false,
      pendingMetricSuggestion: null,
      readyForMetricSuggestion: false,
    });
  },
}));
